import React, { useState, useEffect, useRef } from 'react';

// Intersection Observer hook
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [options]);

  return [ref, isInView];
};

const TinyCloudLanding = () => {
  const [heroRef, heroInView] = useInView();
  const [visionRef, visionInView] = useInView();
  const [howRef, howInView] = useInView();
  const [useCasesRef, useCasesInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  // Retro color palette
  const retroCream = '#f5f0e6';
  const retroBrown = '#5c4033';
  const retroOrange = '#e87d3e';
  const retroGreen = '#4a9c5d';
  const retroPurple = '#7b4397';
  const retroRed = '#d94848';
  const retroYellow = '#f5c142';
  const tcBlue = '#88aaee';

  // Rainbow stripe colors (Apple-inspired)
  const rainbowStripes = [retroGreen, retroYellow, retroOrange, retroRed, retroPurple, tcBlue];

  return (
    <div style={{
      fontFamily: "'DM Sans', sans-serif",
      background: retroCream,
      minHeight: '100vh',
      color: retroBrown,
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=DM+Sans:wght@400;500&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap');

        * {
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        .revelation-fade {
          opacity: 0;
          transform: translateY(32px);
          transition: all 0.7s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .revelation-fade.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .revelation-stagger-1 { transition-delay: 0.1s; }
        .revelation-stagger-2 { transition-delay: 0.2s; }
        .revelation-stagger-3 { transition-delay: 0.35s; }
        .revelation-stagger-4 { transition-delay: 0.5s; }

        .retro-screen {
          background: #1a1a1a;
          border: 8px solid #4a4a4a;
          border-radius: 16px;
          box-shadow: inset 0 0 60px rgba(0,0,0,0.5), 8px 8px 0 ${retroBrown};
        }

        .retro-scanlines::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: repeating-linear-gradient(
            0deg,
            rgba(0, 0, 0, 0.1) 0px,
            rgba(0, 0, 0, 0.1) 1px,
            transparent 1px,
            transparent 3px
          );
          pointer-events: none;
        }

        .retro-blink {
          animation: retroBlink 1s step-end infinite;
        }

        @keyframes retroBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }

        .retro-button-primary {
          background: ${tcBlue};
          color: ${retroBrown};
          border: 3px solid ${retroBrown};
          padding: 1rem 2rem;
          font-family: 'IBM Plex Mono', monospace;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          box-shadow: 6px 6px 0 ${retroBrown};
          text-transform: uppercase;
          text-decoration: none;
          display: inline-block;
          transition: transform 150ms ease, box-shadow 150ms ease;
        }

        .retro-button-primary:hover {
          transform: translate(-2px, -2px);
          box-shadow: 8px 8px 0 ${retroBrown};
        }

        .retro-button-primary:active {
          transform: translate(3px, 3px);
          box-shadow: 3px 3px 0 ${retroBrown};
        }

        .retro-button-secondary {
          background: transparent;
          color: ${retroBrown};
          border: 3px solid ${retroBrown};
          padding: 1rem 2rem;
          font-family: 'IBM Plex Mono', monospace;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          text-transform: uppercase;
          text-decoration: none;
          display: inline-block;
          transition: background 150ms ease;
        }

        .retro-button-secondary:hover {
          background: rgba(92, 64, 51, 0.1);
        }

        .nav-link {
          color: ${retroBrown};
          text-decoration: none;
          font-family: 'IBM Plex Mono', monospace;
          font-weight: 600;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 0.5rem 1rem;
          transition: opacity 150ms ease;
        }

        .nav-link:hover {
          opacity: 0.7;
        }

        @media (max-width: 900px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
          }
          .how-grid {
            grid-template-columns: 1fr !important;
          }
          .use-cases-grid {
            grid-template-columns: 1fr !important;
          }
          .nav-links {
            display: none !important;
          }
        }
      `}</style>

      {/* Nav */}
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: retroCream,
      }}>
        <div style={{
          padding: '1rem 3rem',
          maxWidth: '1600px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <a href="/" style={{
            display: 'flex',
            alignItems: 'center',
            background: '#4573b9',
            padding: '0.1rem 0.75rem',
            borderRadius: '8px',
          }}>
            <svg viewBox="0 0 4074.79 1024" style={{ height: '53px', width: 'auto' }}>
              <path fill="#e8ebbd" d="M114.55,672.25c38.96,13.46,131.72,4.54,178.18,7.17h0l219.15-1.34-.02.31c40.28-2.31,135.28,3.07,164.86,0,95.85-9.95,131.07-135.15,54.27-193.54-39.55-30.07-79.11-19.62-88.67-24.35-.28-.14-.5-.38-.64-.67-27.67-55.58-77.09-90.88-140.21-90.7-.54,0-1.05-.29-1.31-.77-86.32-156.76-314.55-98.27-322.55,71.46h-.09v-.03c-.45,0-.89.02-1.34.03h-1.51v.03c-158.5,2.72-172.71,193.5-60.13,232.39h.01Z"/>
              <path fill="#292f4b" d="M731,484.86c-34.67-26.36-69.34-21.58-83.81-23.19-2.99-.33-5.62-2.13-7-4.79-27.3-52.64-74.5-86.35-134.48-87.7-3.15-.07-6.04-1.77-7.61-4.5-87.85-152.14-312.53-93.24-320.48,75.15h-.09v-.03c-.45,0-.89.02-1.34.03h-1.51v.03c-158.5,2.72-172.71,193.5-60.13,232.39,38.96,13.46,131.72,4.54,178.18,7.17h0l219.15-1.34-.02.31c40.28-2.31,135.28,3.07,164.86,0,95.85-9.95,131.07-135.15,54.27-193.54h.01ZM686.97,655.87c-28.26,7.34-134.99.81-174.08,3.07h-220.16c-37.47-2.11-117.97,2.81-146.43,0-131.66-18.1-97.45-209.52,31.23-196.61.13.01.41.13,1.24.24,3.44.45,7.04,1.16,10.52,1.96,5.37,1.23,9.75-2.23,9.75-7.74v-10.33h0c7.76-186.8,262.88-203.08,297.29-22.87.77,4.03,4.23,7,8.32,7.24,65.83,3.9,98.97,53.49,74.7,111.44-3.85,9.2,7.86,17.09,14.76,9.89,3.93-4.1,6.84-9.79,9.41-17.63,16.05-49.02-12.07-99.19-58.88-117.25-16.63-6.41-24.8-3.93-29.27-9.38-5.25-6.39.7-15.87,8.83-14.37,61.77,11.38,106.41,59.78,103.95,123.37-.25,6.53,6.24,11.25,12.31,8.83,65.64-26.21,104.98,72.8,32.26,92.98-.73.2-1.49.31-2.24.32-81.86,1.32-287.32-.9-376.43-.02-.2,0-.38,0-.58-.01-32.94-1.96-122.57,2.67-143.07.02-45.44-5.89-62.92-55.74-37.38-91.65,21.55-30.3,73.12-30.75,99.84-7.68,9.61,8.29,15.33,20.93,25.47,29.3,7.01,5.79,17.27-1.25,14.35-9.85-13.9-40.97-61.58-61.09-104.33-55.29-85.83,11.99-91.3,141-3.82,155.52.5.08,1.01.12,1.51.12,146.45.07,378.65.27,525.01,0,.38,0,.75-.02,1.13-.07,81.18-9.75,84.15-138.45-14.97-137.58-5.03.04-9.15-3.99-9.15-9.02v-1.93c0-5.14,4.29-9.22,9.43-9.03,121.7,4.51,120.32,150.43,29.48,174.01Z"/>
              <path fill="#292f4b" d="M707.14,272.59q-14.19,0,0,0c-31.93,9.18-39.37,16.62-48.55,48.55q0-14.19,0,0c-9.18-31.93-16.62-39.37-48.55-48.55q14.19,0,0,0c31.93-9.18,39.37-16.62,48.55-48.55q0,14.19,0,0c9.18,31.93,16.62,39.37,48.55,48.55Z"/>
              <path fill="#e8ebbd" d="M692.57,272.59q-9.93,0,0,0c-22.35,6.42-27.56,11.63-33.98,33.98q0-9.93,0,0c-6.42-22.35-11.63-27.56-33.98-33.98q9.93,0,0,0c22.35-6.42,27.56-11.63,33.98-33.98q0,9.93,0,0c6.42,22.35,11.63,27.56,33.98,33.98Z"/>
              <path fill="#292f4b" d="M318.27,321.4c-68.62,16.25-107.69,84.78-90.9,149.82.03.12.03.25.05.38.33,1.88.56,3.87,1.49,5.74,3.18,6.42,10.35,9.35,16,6.54,5.32-2.64,7.41-9.42,4.98-15.56-16.99-59.02,23.04-122.54,90.43-127.61,32.95-2.48,65.12,12.01,84.84,38.53,12.17,16.36,18.69,34.22,20.4,51.92,1.32,13.67,21.88,13.41,22.45-.32.03-.64,0-1.29-.06-1.95-1.55-15.85-6.25-31.81-14.5-47.07-24.22-44.82-85.62-72.15-135.2-60.41h.02Z"/>
              <path fill="#292f4b" d="M728.75,272.59c-45.16,0-68.66,23.5-68.66,68.66,0-45.16-23.5-68.66-68.66-68.66,45.16,0,68.66-23.5,68.66-68.66,0,45.16,23.5,68.66,68.66,68.66h0Z"/>
              <path fill="#fdfad4" d="M708.15,272.59c-31.61,0-48.06,16.45-48.06,48.06,0-31.61-16.45-48.06-48.06-48.06,31.61,0,48.06-16.45,48.06-48.06,0,31.61,16.45,48.06,48.06,48.06Z"/>
              <g>
                <path fill="#e8ebbd" stroke="#292f4b" strokeLinejoin="round" strokeWidth="18" d="M924.8,397.7c0-12.97-10.32-23.29-23.29-23.29h-109.16v-77.62h342.76v77.62h-109.4c-12.97,0-23.29,10.32-23.29,23.29v275.5h-77.62v-275.5Z"/>
                <path fill="#e8ebbd" stroke="#292f4b" strokeLinejoin="round" strokeWidth="18" d="M1180.15,296.74h77.62v376.42h-77.62v-376.42Z"/>
                <path fill="#e8ebbd" stroke="#292f4b" strokeLinejoin="round" strokeWidth="18" d="M1310.76,385.74c0-49.28,40.02-89.01,89.01-89.01h49.81c48.99,0,88.76,39.73,88.76,89.01v186.74c0,12.73,10.61,23.29,23.29,23.29h25.94c12.97,0,23.29-10.61,23.29-23.29v-275.74h77.62v287.7c0,48.99-39.73,88.76-89.01,88.76h-49.81c-48.99,0-88.76-39.73-88.76-88.76v-186.74c0-12.97-10.32-23.29-23.29-23.29h-25.94c-12.97,0-23.29,10.32-23.29,23.29v275.5h-77.62v-287.45Z"/>
                <path fill="#e8ebbd" stroke="#292f4b" strokeLinejoin="round" strokeWidth="18" d="M2003.76,422.86v-126.08h77.62v137.99c0,49.28-39.73,89.01-89.01,89.01h-43.44v149.42h-77.62v-149.42h-43.44c-49.28,0-89.01-39.73-89.01-89.01v-137.99h77.62v126.08c0,12.97,10.32,23.29,23.29,23.29h140.93c12.73,0,23.05-10.32,23.05-23.29h0Z"/>
                <path fill="#e8ebbd" stroke="#292f4b" strokeLinejoin="round" strokeWidth="18" d="M2206.65,572.52c0,6.08,2.37,11.91,6.61,16.44,4.49,4.24,10.32,6.89,16.68,6.89h241.6v77.34h-253.51c-49.28,0-89.01-39.73-89.01-88.76v-198.69c0-49.28,39.73-89.01,89.01-89.01h253.51v77.62h-241.6c-12.97,0-23.29,10.32-23.29,23.29v174.87Z"/>
                <path fill="#e8ebbd" stroke="#292f4b" strokeLinejoin="round" strokeWidth="18" d="M2602.15,296.74v275.74c0,12.73,10.32,23.29,23.29,23.29h241.6v77.34h-253.51c-49.28,0-89.01-39.73-89.01-88.76v-287.7h77.62v.08Z"/>
                <path fill="#e8ebbd" stroke="#292f4b" strokeLinejoin="round" strokeWidth="18" d="M3243.99,385.74v198.69c0,48.99-39.73,88.76-89.01,88.76h-164.51c-49.28,0-89.01-39.73-89.01-88.76v-198.69c0-49.28,39.73-89.01,89.01-89.01h164.51c49.28,0,89.01,39.73,89.01,89.01ZM3143.08,374.36h-140.65c-12.97,0-23.29,10.32-23.29,23.29v174.83c0,6.08,2.37,11.91,6.61,16.44,4.49,4.24,10.32,6.89,16.68,6.89h140.65c6.36,0,12.2-2.65,16.68-6.89,4.24-4.49,6.61-10.32,6.61-16.44v-174.83c0-12.97-10.32-23.29-23.29-23.29Z"/>
                <path fill="#e8ebbd" stroke="#292f4b" strokeLinejoin="round" strokeWidth="18" d="M3559.22,572.52v-275.74h77.62v287.7c0,48.99-39.73,88.76-89.01,88.76h-164.51c-49.28,0-89.01-39.73-89.01-88.76v-287.7h77.62v275.74c0,12.73,10.32,23.29,23.29,23.29h140.93c12.73,0,23.05-10.61,23.05-23.29h0Z"/>
                <path fill="#e8ebbd" stroke="#292f4b" strokeLinejoin="round" strokeWidth="18" d="M3689.84,673.16v-376.42h255.35c48.21,0,87.17,39.2,87.17,87.17v202.12c0,48.21-38.96,87.17-87.17,87.17h-255.35v-.04ZM3954.73,398.76c0-13.5-10.85-24.35-24.35-24.35h-162.92v221.45h162.92c13.5,0,24.35-11.14,24.35-24.64v-172.46Z"/>
              </g>
            </svg>
          </a>
          <div className="nav-links" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <a href="https://docs.tinycloud.xyz" className="nav-link">
              For Enterprises
            </a>
            <a href="/investors" className="nav-link">
              For Investors
            </a>
            <a
              href="/meet-sam"
              className="retro-button-primary"
              style={{ padding: '0.625rem 1.25rem', fontSize: '0.875rem', boxShadow: `4px 4px 0 ${retroBrown}` }}
            >
              Book a Consultation
            </a>
          </div>
        </div>
        {/* Rainbow stripe */}
        <div style={{ display: 'flex', height: '8px' }}>
          {rainbowStripes.map((color, i) => (
            <div key={i} style={{ flex: 1, background: color }} />
          ))}
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section
        ref={heroRef}
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '10rem 2rem 4rem',
          position: 'relative',
        }}
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%', padding: '0 1rem' }}>
          <div style={{ maxWidth: '700px' }}>
            <div>
              <div className={`revelation-fade revelation-stagger-1 ${heroInView ? 'visible' : ''}`}>
                <span style={{
                  display: 'inline-block',
                  padding: '0.5rem 1rem',
                  background: retroYellow,
                  color: retroBrown,
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  marginBottom: '2rem',
                  border: `3px solid ${retroBrown}`,
                  boxShadow: `4px 4px 0 ${retroBrown}`,
                }}>
                  Cryptographically Verified Privacy
                </span>
              </div>

              <h1
                className={`revelation-fade revelation-stagger-2 ${heroInView ? 'visible' : ''}`}
                style={{
                  fontFamily: "'Sora', sans-serif",
                  fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                  fontWeight: 800,
                  lineHeight: 1.1,
                  letterSpacing: '-0.03em',
                  marginBottom: '2rem',
                  color: retroBrown,
                }}
              >
                AI that <span style={{ color: tcBlue }}>never sees your data.</span>
              </h1>

              <p
                className={`revelation-fade revelation-stagger-3 ${heroInView ? 'visible' : ''}`}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  color: retroBrown,
                  marginBottom: '2rem',
                  maxWidth: '520px',
                }}
              >
                Not "we promise not to look." Mathematically impossible to access—even for us.
                <br /><br />
                Storage you own. AI that's verifiably private. Finally use the tools everyone else takes for granted.
              </p>

              <div
                className={`revelation-fade revelation-stagger-4 ${heroInView ? 'visible' : ''}`}
                style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}
              >
                <a href="/meet-sam" className="retro-button-primary">
                  Book a Consultation
                </a>
                <a href="https://docs.tinycloud.xyz" className="retro-button-secondary">
                  Read Docs
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rainbow divider */}
      <div style={{ display: 'flex', height: '12px' }}>
        {rainbowStripes.map((color, i) => (
          <div key={i} style={{ flex: 1, background: color }} />
        ))}
      </div>

      {/* ============ VISION ============ */}
      <section
        ref={visionRef}
        style={{
          padding: '6rem 2rem',
          background: '#fff',
        }}
      >
        <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
          <div className={`revelation-fade ${visionInView ? 'visible' : ''}`} style={{ marginBottom: '3rem' }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: '0.875rem',
              color: retroBrown,
              opacity: 0.6,
              marginBottom: '1rem',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}>
              The Confidentiality Problem
            </div>
            <h2 style={{
              fontFamily: "'Sora', sans-serif",
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 700,
              letterSpacing: '-0.03em',
              color: retroBrown,
              marginBottom: '2rem',
            }}>
              Your profession requires discretion. Current AI tools require the opposite.
            </h2>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: '1rem',
              lineHeight: 2,
              color: retroBrown,
              opacity: 0.85,
            }}>
              <p style={{ marginBottom: '1.5rem' }}>
                You can't use ChatGPT with client data. You can't record meetings with Granola when discussing sensitive matters. You can't let Fireflies transcribe a privileged conversation.
              </p>
              <p style={{ marginBottom: '1.5rem' }}>
                Not because the tools aren't useful. Because every platform can see what you give them—and you have no way to verify otherwise.
              </p>
              <p style={{ marginBottom: '1.5rem' }}>
                TinyCloud changes the equation. Your data stays in storage you own. AI runs in hardware-enforced isolation. We can prove what code ran. We can prove we never saw it.
              </p>
              <p>
                Not "trust us." Verify us.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Rainbow divider */}
      <div style={{ display: 'flex', height: '8px' }}>
        {rainbowStripes.map((color, i) => (
          <div key={i} style={{ flex: 1, background: color }} />
        ))}
      </div>

      {/* ============ HOW IT WORKS ============ */}
      <section
        ref={howRef}
        style={{
          padding: '6rem 2rem',
          background: retroCream,
        }}
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div className={`revelation-fade ${howInView ? 'visible' : ''}`} style={{ marginBottom: '4rem' }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: '0.875rem',
              color: retroBrown,
              opacity: 0.6,
              marginBottom: '1rem',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}>
              How It Works
            </div>
            <h2 style={{
              fontFamily: "'Sora', sans-serif",
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 700,
              letterSpacing: '-0.03em',
              color: retroBrown,
            }}>
              How verified privacy actually works.
            </h2>
          </div>

          <div
            className={`revelation-fade revelation-stagger-2 ${howInView ? 'visible' : ''} how-grid`}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '1.5rem',
            }}
          >
            {/* Sovereign Storage */}
            <div style={{
              background: '#fff',
              border: `3px solid ${retroBrown}`,
              borderRadius: '8px',
              padding: '2rem',
              boxShadow: `6px 6px 0 ${retroBrown}`,
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: tcBlue,
                border: `3px solid ${retroBrown}`,
                borderRadius: '8px',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: "'IBM Plex Mono', monospace",
                fontWeight: 700,
                fontSize: '1.25rem',
                color: retroBrown,
              }}>
                01
              </div>
              <h3 style={{
                fontFamily: "'Sora', sans-serif",
                fontSize: '1.25rem',
                fontWeight: 700,
                marginBottom: '1rem',
                color: retroBrown,
              }}>
                Your Data, Your Control
              </h3>
              <p style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '0.875rem',
                lineHeight: 1.8,
                color: retroBrown,
                opacity: 0.8,
              }}>
                Your data is protected by private keys that only you hold. Files and transcripts live encrypted in storage you control, not on a provider's servers.
              </p>
            </div>

            {/* Private Compute */}
            <div style={{
              background: '#fff',
              border: `3px solid ${retroBrown}`,
              borderRadius: '8px',
              padding: '2rem',
              boxShadow: `6px 6px 0 ${retroBrown}`,
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: retroYellow,
                border: `3px solid ${retroBrown}`,
                borderRadius: '8px',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: "'IBM Plex Mono', monospace",
                fontWeight: 700,
                fontSize: '1.25rem',
                color: retroBrown,
              }}>
                02
              </div>
              <h3 style={{
                fontFamily: "'Sora', sans-serif",
                fontSize: '1.25rem',
                fontWeight: 700,
                marginBottom: '1rem',
                color: retroBrown,
              }}>
                AI in Isolation
              </h3>
              <p style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '0.875rem',
                lineHeight: 1.8,
                color: retroBrown,
                opacity: 0.8,
              }}>
                AI runs inside a trusted execution environment (TEE)—a secure enclave built into the chip itself. Think of it as a locked room where computation happens. Your data goes in, insights come out, but nothing and no one can see what's inside—not even us.
              </p>
            </div>

            {/* Scoped Permissions */}
            <div style={{
              background: '#fff',
              border: `3px solid ${retroBrown}`,
              borderRadius: '8px',
              padding: '2rem',
              boxShadow: `6px 6px 0 ${retroBrown}`,
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: retroGreen,
                border: `3px solid ${retroBrown}`,
                borderRadius: '8px',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: "'IBM Plex Mono', monospace",
                fontWeight: 700,
                fontSize: '1.25rem',
                color: '#fff',
              }}>
                03
              </div>
              <h3 style={{
                fontFamily: "'Sora', sans-serif",
                fontSize: '1.25rem',
                fontWeight: 700,
                marginBottom: '1rem',
                color: retroBrown,
              }}>
                Audit Everything
              </h3>
              <p style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '0.875rem',
                lineHeight: 1.8,
                color: retroBrown,
                opacity: 0.8,
              }}>
                Every access logged. Every operation traceable. Every permission revocable. When regulators ask, you have answers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ VERIFIED VS TRUST ============ */}
      <section
        style={{
          padding: '6rem 2rem',
          background: '#fff',
        }}
      >
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: '0.875rem',
            color: retroBrown,
            opacity: 0.6,
            marginBottom: '1rem',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}>
            The Difference
          </div>
          <h2 style={{
            fontFamily: "'Sora', sans-serif",
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 700,
            letterSpacing: '-0.03em',
            color: retroBrown,
            marginBottom: '2rem',
          }}>
            Verified private. Not "trust us" private.
          </h2>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: '1rem',
            lineHeight: 2,
            color: retroBrown,
            opacity: 0.85,
          }}>
            <p style={{ marginBottom: '1.5rem' }}>
              Every AI platform asks you to assess their trustworthiness. Read the terms. Hope they mean it. Trust that nothing changes when they get acquired.
            </p>
            <p style={{ marginBottom: '1.5rem' }}>
              That's how privacy works everywhere else. Policy-based. Promise-based. Reversible.
            </p>
            <p style={{ marginBottom: '1.5rem' }}>
              TinyCloud is different. We use hardware that makes access mathematically impossible—not just against attackers, but against us. We can prove what code ran on your data. We can prove we never saw it.
            </p>
            <p style={{ marginBottom: '1.5rem' }}>
              You don't assess our trustworthiness. You verify our claims.
            </p>
            <p style={{
              fontWeight: 600,
              color: retroBrown,
            }}>
              Current platforms can see your data. We can't. That's not a policy. It's architecture.
            </p>
          </div>
        </div>
      </section>

      {/* Rainbow divider */}
      <div style={{ display: 'flex', height: '8px' }}>
        {rainbowStripes.map((color, i) => (
          <div key={i} style={{ flex: 1, background: color }} />
        ))}
      </div>

      {/* ============ USE CASES ============ */}
      <section
        ref={useCasesRef}
        style={{
          padding: '6rem 2rem',
          background: '#fff',
        }}
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div className={`revelation-fade ${useCasesInView ? 'visible' : ''}`} style={{ marginBottom: '4rem' }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: '0.875rem',
              color: retroBrown,
              opacity: 0.6,
              marginBottom: '1rem',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}>
              Use Cases
            </div>
            <h2 style={{
              fontFamily: "'Sora', sans-serif",
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 700,
              letterSpacing: '-0.03em',
              color: retroBrown,
            }}>
              What confidential AI actually looks like.
            </h2>
          </div>

          <div
            className={`revelation-fade revelation-stagger-2 ${useCasesInView ? 'visible' : ''} use-cases-grid`}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '1.5rem',
            }}
          >
            {[
              {
                title: 'Legal Research',
                desc: 'Review contracts. Summarize depositions. Search case files. AI-powered legal work without exposing privileged communications.',
                color: tcBlue,
              },
              {
                title: 'Financial Analysis',
                desc: 'Analyze portfolios. Summarize client meetings. Research investments. Your fiduciary duty stays intact.',
                color: retroYellow,
              },
              {
                title: 'Medical Documentation',
                desc: 'Transcribe patient notes. Summarize records. Research treatment options. HIPAA-compliant by architecture, not policy.',
                color: retroOrange,
              },
              {
                title: 'Executive Communications',
                desc: 'Record board meetings. Draft confidential memos. Brief preparation from sensitive materials. MNPI stays non-public.',
                color: retroGreen,
              },
            ].map((item, index) => (
              <div key={index} style={{
                background: retroCream,
                border: `3px solid ${retroBrown}`,
                borderRadius: '8px',
                padding: '2rem',
                boxShadow: `6px 6px 0 ${retroBrown}`,
                display: 'flex',
                gap: '1.5rem',
                alignItems: 'flex-start',
              }}>
                <div style={{
                  width: '12px',
                  height: '48px',
                  background: item.color,
                  border: `2px solid ${retroBrown}`,
                  borderRadius: '4px',
                  flexShrink: 0,
                }} />
                <div>
                  <h3 style={{
                    fontFamily: "'Sora', sans-serif",
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    marginBottom: '0.75rem',
                    color: retroBrown,
                  }}>
                    {item.title}
                  </h3>
                  <p style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: '0.875rem',
                    lineHeight: 1.8,
                    color: retroBrown,
                    opacity: 0.8,
                    margin: 0,
                  }}>
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rainbow divider */}
      <div style={{ display: 'flex', height: '12px' }}>
        {rainbowStripes.map((color, i) => (
          <div key={i} style={{ flex: 1, background: color }} />
        ))}
      </div>

      {/* ============ CTA ============ */}
      <section
        ref={ctaRef}
        style={{
          padding: '6rem 2rem',
          background: tcBlue,
        }}
      >
        <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
          <h2
            className={`revelation-fade revelation-stagger-1 ${ctaInView ? 'visible' : ''}`}
            style={{
              fontFamily: "'Sora', sans-serif",
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 700,
              letterSpacing: '-0.03em',
              color: retroBrown,
              marginBottom: '1.5rem',
            }}
          >
            Your practice deserves AI too.
          </h2>

          <p
            className={`revelation-fade revelation-stagger-2 ${ctaInView ? 'visible' : ''}`}
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: '1rem',
              lineHeight: 1.8,
              color: retroBrown,
              opacity: 0.8,
              marginBottom: '2.5rem',
            }}
          >
            Stop being left behind while everyone else gets more productive.
          </p>

          <div
            className={`revelation-fade revelation-stagger-3 ${ctaInView ? 'visible' : ''}`}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}
          >
            <a
              href="/meet-sam"
              style={{
                background: retroYellow,
                color: retroBrown,
                border: `3px solid ${retroBrown}`,
                padding: '1rem 2rem',
                fontFamily: "'IBM Plex Mono', monospace",
                fontWeight: 600,
                fontSize: '1rem',
                cursor: 'pointer',
                boxShadow: `6px 6px 0 ${retroBrown}`,
                textTransform: 'uppercase',
                textDecoration: 'none',
              }}
            >
              Book a Consultation
            </a>
            <p
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '0.875rem',
                color: retroBrown,
                opacity: 0.7,
                margin: 0,
              }}
            >
              Currently onboarding select firms and practices.
            </p>
          </div>

          <div
            className={`revelation-fade revelation-stagger-4 ${ctaInView ? 'visible' : ''}`}
            style={{ display: 'flex', gap: '2rem', justifyContent: 'center' }}
          >
            <a
              href="https://docs.tinycloud.xyz"
              style={{
                color: retroBrown,
                textDecoration: 'none',
                fontFamily: "'IBM Plex Mono', monospace",
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                opacity: 0.8,
              }}
            >
              For Enterprises
            </a>
            <a
              href="/investors"
              style={{
                color: retroBrown,
                textDecoration: 'none',
                fontFamily: "'IBM Plex Mono', monospace",
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                opacity: 0.8,
              }}
            >
              For Investors
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        background: '#1a1a1a',
        color: '#fafafa',
        padding: '3rem 2rem',
        borderTop: `4px solid ${retroBrown}`,
      }}>
        <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '2rem',
            marginBottom: '2rem',
          }}>
            {/* Logo */}
            <div>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontWeight: 700,
                fontSize: '1.25rem',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  background: `linear-gradient(135deg, ${retroGreen} 0%, ${retroYellow} 25%, ${retroOrange} 50%, ${retroRed} 75%, ${retroPurple} 100%)`,
                  border: '2px solid #fafafa',
                  borderRadius: '3px',
                }} />
                TinyCloud
              </div>
              <p style={{ fontSize: '0.875rem', opacity: 0.7 }}>
                Private AI. Sovereign storage.
              </p>
            </div>

            {/* Platform */}
            <div>
              <h4 style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontWeight: 700,
                fontSize: '0.75rem',
                marginBottom: '1rem',
                color: tcBlue,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}>
                Platform
              </h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a href="https://docs.tinycloud.xyz" style={{ color: '#fafafa', textDecoration: 'none', fontSize: '0.875rem', opacity: 0.8 }}>
                    Documentation
                  </a>
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a href="/protocol" style={{ color: '#fafafa', textDecoration: 'none', fontSize: '0.875rem', opacity: 0.8 }}>
                    Protocol
                  </a>
                </li>
              </ul>
            </div>

            {/* Connect */}
            <div>
              <h4 style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontWeight: 700,
                fontSize: '0.75rem',
                marginBottom: '1rem',
                color: retroYellow,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}>
                Connect
              </h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a href="https://twitter.com/TinyCloudLabs" style={{ color: '#fafafa', textDecoration: 'none', fontSize: '0.875rem', opacity: 0.8 }}>
                    X/Twitter
                  </a>
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a href="https://github.com/TinyCloudLabs" style={{ color: '#fafafa', textDecoration: 'none', fontSize: '0.875rem', opacity: 0.8 }}>
                    GitHub
                  </a>
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a href="https://t.me/+pplkv1XbbU01MDVh" style={{ color: '#fafafa', textDecoration: 'none', fontSize: '0.875rem', opacity: 0.8 }}>
                    Telegram
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontWeight: 700,
                fontSize: '0.75rem',
                marginBottom: '1rem',
                color: retroOrange,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}>
                Legal
              </h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a href="/privacy" style={{ color: '#fafafa', textDecoration: 'none', fontSize: '0.875rem', opacity: 0.8 }}>
                    Privacy Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div style={{
            borderTop: '1px solid rgba(250,250,250,0.1)',
            paddingTop: '1.5rem',
            textAlign: 'center',
          }}>
            <p style={{ fontSize: '0.75rem', opacity: 0.5, fontFamily: "'IBM Plex Mono', monospace" }}>
              © 2026 TinyCloud Labs. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TinyCloudLanding;
